import { lazy, Suspense, type ReactNode } from 'react';
import { RouteFallback, readDocumentLocale, useApi, type ConsentGap } from '@agnivo/app-core';

// The step is a lazy chunk: nearly every session never sees a consent refusal, so the entry chunk must
// not pay for it (measured in the wiring MR).
const AcceptanceStep = lazy(() =>
  import('./AcceptanceStep').then((m) => ({ default: m.AcceptanceStep })),
);

/**
 * Renders the consent re-ask IN PLACE OF the app while the server has refused with CONSENT_REQUIRED /
 * PROFILE_INCOMPLETE, then remounts the app (its screens refetch — that is the one retry of the
 * original action). Mounted INSIDE `IdentityGapGate` (a session with no platform user must see the
 * identity gap, never a consent screen) and INSIDE `ApiProvider` (needs the client).
 *
 * The profile form is a later slice: for now a step that needs profile fields keeps showing the same
 * step (`onNeedProfile` unset), never a wrong-looking success.
 */
export function ConsentGate({ gap, children }: { gap: ConsentGap; children: ReactNode }) {
  const { client } = useApi();
  if (!gap.pending) return <>{children}</>;
  return (
    <div data-consent-gate="true" className="mx-auto flex h-full max-w-md flex-col justify-center p-6">
      <Suspense fallback={<RouteFallback />}>
        <AcceptanceStep
          step={gap.pending}
          lang={readDocumentLocale() === 'en' ? 'en' : 'ru'}
          consents={client.consents}
          onDone={gap.resolve}
        />
      </Suspense>
    </div>
  );
}
